import { recommend } from '@theme/kyn-fit-finder';

/**
 * <kyn-size-guide> — the size guide modal on the product page (snippets/kyn-size-guide.liquid).
 * Native <dialog> (focus trap, Esc to close), tabs with arrow-key navigation, "Znajdź rozmiar"
 * (height + weight -> size, remembered in this browser; "Wybierz" picks it on the product page), a
 * cm / in switch that is remembered in this browser, and the size currently picked on the product
 * page highlighted in the table.
 */

const UNIT_KEY = 'kyn:size-unit';
const FIT_KEY = 'kyn:fit-answers';

/** @returns {{ height: number, weight: number } | null} */
function readAnswers() {
  try {
    const data = JSON.parse(window.localStorage.getItem(FIT_KEY) || 'null');
    return data && data.height && data.weight ? data : null;
  } catch {
    return null;
  }
}

/** @param {{ height: number, weight: number }} answers */
function saveAnswers(answers) {
  try {
    window.localStorage.setItem(FIT_KEY, JSON.stringify(answers));
  } catch {
    // Storage blocked: the answer still shows for this page view.
  }
}
const CM_PER_INCH = 2.54;

function readUnit() {
  try {
    return window.localStorage.getItem(UNIT_KEY) === 'in' ? 'in' : 'cm';
  } catch {
    return 'cm';
  }
}

/** @param {string} unit */
function saveUnit(unit) {
  try {
    window.localStorage.setItem(UNIT_KEY, unit);
  } catch {
    // Storage blocked: the switch still works for this page view.
  }
}

class KynSizeGuide extends HTMLElement {
  connectedCallback() {
    this.dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-sg-dialog]'));
    this.querySelector('[data-sg-open]')?.addEventListener('click', this.#open);
    this.querySelector('[data-sg-close]')?.addEventListener('click', this.#close);
    // A click on the backdrop lands on the dialog element itself.
    this.dialog?.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.#close();
    });

    const tabs = [...this.querySelectorAll('[data-sg-tab]')];
    for (const tab of tabs) {
      tab.addEventListener('click', () => this.#select(tab));
      tab.addEventListener('keydown', (event) => {
        const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        event.preventDefault();
        const next = tabs[(tabs.indexOf(tab) + step + tabs.length) % tabs.length];
        this.#select(next);
        /** @type {HTMLElement} */ (next).focus();
      });
    }

    for (const input of this.querySelectorAll('[data-sg-unit]')) {
      input.addEventListener('change', () => {
        const unit = /** @type {HTMLInputElement} */ (input).value;
        saveUnit(unit);
        this.#applyUnit(unit);
      });
    }

    const find = /** @type {HTMLFormElement | null} */ (this.querySelector('[data-sg-find]'));
    find?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#findSize(true);
    });
    this.querySelector('[data-sg-pick]')?.addEventListener('click', this.#pickSize);

    // "Pokaż wszystkie wymiary": the table opens with the key measurements only.
    const more = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-sg-more]'));
    more?.addEventListener('click', () => {
      const all = more.getAttribute('aria-expanded') !== 'true';
      more.setAttribute('aria-expanded', String(all));
      more.textContent = (all ? more.dataset.less : more.dataset.more) || '';
      this.querySelector('.kyn-sg__table')?.toggleAttribute('data-sg-all', all);
    });
  }

  #open = () => {
    if (!this.dialog) return;
    const unit = readUnit();
    const radio = this.querySelector(`[data-sg-unit][value="${unit}"]`);
    if (radio instanceof HTMLInputElement) radio.checked = true;
    this.#applyUnit(unit);
    this.#highlightCurrentSize();
    this.#restoreAnswers();
    this.dialog.showModal();
  };

  /** Fills the form with the answers given on an earlier product and shows the result straight away. */
  #restoreAnswers() {
    const answers = readAnswers();
    const form = /** @type {HTMLFormElement | null} */ (this.querySelector('[data-sg-find]'));
    if (!answers || !form) return;
    /** @type {HTMLInputElement} */ (form.elements.namedItem('height')).value = String(answers.height);
    /** @type {HTMLInputElement} */ (form.elements.namedItem('weight')).value = String(answers.weight);
    this.#findSize(false);
  }

  /** The chart as printed in the "Wymiary" table: sizes and flat cm per measurement. */
  #chart() {
    const sizes = [...this.querySelectorAll('thead [data-sg-size]')].map((th) => /** @type {HTMLElement} */ (th).dataset.sgSize || '');
    /** @type {Record<string, (number | null)[]>} */
    const rows = {};
    for (const row of this.querySelectorAll('tbody tr[data-sg-key]')) {
      rows[/** @type {HTMLElement} */ (row).dataset.sgKey || ''] = [...row.querySelectorAll('td')].map(
        (cell) => Number(/** @type {HTMLElement} */ (cell).dataset.cm) || null
      );
    }
    const kind = this.dialog?.dataset.sgKind === 'bottom' ? 'bottom' : 'top';
    return { kind: /** @type {'top' | 'bottom'} */ (kind), sizes, rows };
  }

  /** @param {boolean} save - remember the answers (on submit, not when restoring) */
  #findSize(save) {
    const form = /** @type {HTMLFormElement | null} */ (this.querySelector('[data-sg-find]'));
    const result = /** @type {HTMLElement | null} */ (this.querySelector('[data-sg-result]'));
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-sg-find-error]'));
    if (!form || !result || !error) return;
    const height = Number(/** @type {HTMLInputElement} */ (form.elements.namedItem('height')).value);
    const weight = Number(/** @type {HTMLInputElement} */ (form.elements.namedItem('weight')).value);
    const valid = height >= 140 && height <= 210 && weight >= 35 && weight <= 160;
    error.hidden = valid;
    if (!valid) {
      result.hidden = true;
      return;
    }
    if (save) saveAnswers({ height, weight });

    const { results, best, note } = recommend(this.#chart(), height, weight);
    /** @type {HTMLElement} */ (result.querySelector('[data-sg-best]')).textContent = best || '—';
    const noteEl = /** @type {HTMLElement} */ (result.querySelector('[data-sg-note]'));
    noteEl.textContent = note ? result.getAttribute(`data-note-${note}`) || '' : '';
    noteEl.hidden = !note;

    const list = /** @type {HTMLElement} */ (result.querySelector('[data-sg-verdicts]'));
    list.replaceChildren(
      ...results.map(({ size, verdict }) => {
        const item = document.createElement('li');
        item.dataset.verdict = verdict;
        if (size === best) item.setAttribute('data-best', '');
        const label = document.createElement('b');
        label.textContent = size;
        item.append(label, result.getAttribute(`data-v-${verdict}`) || '');
        return item;
      })
    );

    const pick = /** @type {HTMLButtonElement} */ (result.querySelector('[data-sg-pick]'));
    const input = best ? this.#sizeInput(best) : null;
    pick.hidden = !input;
    pick.dataset.size = best || '';
    pick.textContent = (result.dataset.pick || '').replace('__SIZE__', best || '');
    result.hidden = false;
  }

  /** The product form's option input for a size, when this modal sits on a product page. @param {string} size */
  #sizeInput(size) {
    const scope = this.closest('.shopify-section') ?? document;
    return [...scope.querySelectorAll('variant-picker input[type="radio"]')].find(
      (input) => /** @type {HTMLInputElement} */ (input).value === size
    );
  }

  #pickSize = () => {
    const size = /** @type {HTMLElement | null} */ (this.querySelector('[data-sg-pick]'))?.dataset.size;
    const input = size ? /** @type {HTMLInputElement | undefined} */ (this.#sizeInput(size)) : undefined;
    if (!input) return;
    if (!input.checked) input.click();
    this.#close();
  };

  #close = () => {
    this.dialog?.close();
  };

  /** @param {Element} tab */
  #select(tab) {
    for (const other of this.querySelectorAll('[data-sg-tab]')) {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.setAttribute('tabindex', selected ? '0' : '-1');
      const panel = this.querySelector(`#${other.getAttribute('aria-controls')}`);
      if (panel instanceof HTMLElement) panel.hidden = !selected;
    }
  }

  /** @param {string} unit */
  #applyUnit(unit) {
    const format = new Intl.NumberFormat(document.documentElement.lang || 'en', { maximumFractionDigits: 1 });
    for (const cell of this.querySelectorAll('td[data-cm]')) {
      const cm = Number(/** @type {HTMLElement} */ (cell).dataset.cm);
      if (!cm) continue;
      const value = unit === 'in' ? Math.round((cm / CM_PER_INCH) * 2) / 2 : cm;
      cell.textContent = format.format(value);
    }
  }

  /** Marks the column of the size chosen in the product form, if it is one of the chart's sizes. */
  #highlightCurrentSize() {
    const sizes = [...this.querySelectorAll('thead [data-sg-size]')].map((th) => /** @type {HTMLElement} */ (th).dataset.sgSize);
    const scope = this.closest('.shopify-section') ?? document;
    const chosen = [...scope.querySelectorAll('variant-picker input:checked, variant-picker select')]
      .map((el) => /** @type {HTMLInputElement | HTMLSelectElement} */ (el).value)
      .find((value) => sizes.includes(value));
    for (const cell of this.querySelectorAll('[data-sg-size]')) {
      cell.toggleAttribute('data-sg-current', Boolean(chosen) && /** @type {HTMLElement} */ (cell).dataset.sgSize === chosen);
    }
  }
}

if (!customElements.get('kyn-size-guide')) customElements.define('kyn-size-guide', KynSizeGuide);
